<template>

	<PageDynamic :title="pageTitle" :description="pageDescription" :verification="pageVerification" :path="route.path">

		<NuxtLoadingVdo @videoEnded="onVideoEnded" />

		<div class="block-video block-video--home">

			<div v-if="pageContent" v-html="pageContent"></div>

			<PagesHomeBillboard ref="billboardRef" />
			<PagesHomeWork />
			<PagesHomeOverview />

		</div>

		<!-- <PagesHomeInformation /> -->

		<PagesHomePortfolio />
		<PagesHomeService />
		<PagesHomeConnect />

	</PageDynamic>

</template>

<script setup>

import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { usePageContentStore } from "@/stores/pageContent";
import { useShortcodeParser } from "~/composables/useShortcodeParser";
import PageDynamic from "@/components/pages/Dynamic.vue";
import NuxtLoadingVdo from '@/components/NuxtLoadingVdo.vue';


const store = usePageContentStore();
const route = useRoute();
const { parseShortcodes } = useShortcodeParser();

// SSR-safe metadata fetch (title/description only, no shortcode parsing) so
// the server-rendered <title>/description reflect the real CMS values
// (alternate_title/excerpt) instead of being empty until onMounted below runs.
await store.fetchPageMeta(route.path);

const pageTitle = computed(() => store.title);
const pageDescription = computed(() => store.description);
const pageVerification = computed(() => store.verification);
const pageContent = ref("");

const billboardRef = ref(null);



const onVideoEnded = () => {
  if (billboardRef.value) {
    billboardRef.value.onVideoEnded();
  }
};

const loadScript = (src, isAsync = true, isDefer = true) => {
	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = src;
		script.async = isAsync;
		script.defer = isDefer;
		script.onload = resolve;
		script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
		document.head.appendChild(script);
	});
};

const injectCustomHead = async (customHead) => {
	const head = document.head;
	const headFragment = document.createRange().createContextualFragment(customHead);
	head.appendChild(headFragment);
};

const injectCustomFooter = async (customFooter) => {
	const body = document.body;
	const footerFragment = document.createRange().createContextualFragment(customFooter);

	
	const scripts = footerFragment.querySelectorAll('script[src]');
	const scriptUrls = Array.from(scripts).map((script) => script.src);


	scripts.forEach((script) => script.remove());
	body.appendChild(footerFragment);

	
	for (const src of scriptUrls) {
		try {
			await loadScript(src);
		} catch (error) {
			console.error(`Failed to load script: ${src}`, error);
		}
	}
};

// Fetch the page content based on the route's slug
const fetchPageContent = async () => {
	try {

		const path = route.path;
		await store.fetchPageContent(path);

		pageContent.value = store.content;

		if (store.content.custom_head) {
			injectCustomHead(store.content.custom_head);
		}
		if (store.content.custom_footer) {
			injectCustomFooter(store.content.custom_footer);
		}

	} catch (error) {
		console.error("Error fetching page content:", error);
	}
};


onMounted(() => {
	fetchPageContent();
});

</script>

<style lang="scss">
@import "@/assets/scss/app";

.container-full-height {
	position: absolute;
	top: 8%;
	left: 50%;
	transform: translateX(-50%);
	height: calc(100% - 13%);
	pointer-events: none;
	z-index: 2;

	@include media-breakpoint-up(xl) {
		height: calc(100% - 13%);
	}

	@include media-breakpoint-down(md) {
		position: relative;
		top: 0;
		left: 0;
		transform: none;
	}
}

.block-video--home {
	position: relative;

	.block-video__content {
		position: sticky;
		top: 9rem;
		left: 0;
		z-index: 1;
		pointer-events: auto;
		padding-right: 5.62rem;

		@include media-breakpoint-down(lg) {
			padding-right: 2rem;
			top: 9rem;
		}

		@include media-breakpoint-down(md) {
			position: relative;
			top: 0;
			left: 0;
			padding-right: 0;
			padding-top: 8rem;
		}

		@include media-breakpoint-down(sm) {
			padding-top: 6.45rem;
		}
	}

	.block-video__column {
		position: absolute;
		height: 100%;
		top: 0;
		left: 0;

		@include media-breakpoint-down(md) {
			position: relative;
		}
	}
}
</style>